/**
 * Alloc Warrior — Story graph
 *
 * Node types:
 *   - narrative: { text, next }
 *   - choice:    { text, choices: [{ text, next }] }
 *   - combat:    { text, combat: { bossId, onWin, onLose } }
 *   - ending:    { ending: { id, title, type, text } }
 *
 * Speakers:
 *   - 'narrator' (no portrait, italics)
 *   - 'self' (player portrait)
 *   - bossId (boss portrait)
 */

export const START_NODE_ID = 'opening_letter';

export const ACTS = {
  ACT_1: 'Acte 1 — Le Recommandé',
  ACT_2: 'Acte 2 — Le Périple',
  ACT_3: 'Acte 3 — Le Bureau',
  ACT_4: 'Acte 4 — La Sentence',
  ENDINGS: 'Fins',
};

export const storyNodes = {
  // ============ ACT 1 — Opening ============

  opening_letter: {
    id: 'opening_letter',
    act: ACTS.ACT_1,
    treeLabel: 'La Lettre',
    type: 'narrative',
    speaker: 'narrator',
    text: "Samedi, 8h12. Le facteur sonne, agressif. Tu signes l'accusé de réception sans regarder. Sur l'enveloppe : « CAF — RECOMMANDÉ AVEC AR ». Tu déchires. Tu lis. Tes yeux s'arrêtent sur un chiffre : 3 247 €. À rembourser sous trente jours.",
    next: 'opening_letter_2',
  },

  opening_letter_2: {
    id: 'opening_letter_2',
    act: ACTS.ACT_1,
    treeLabel: '3 247 €',
    type: 'narrative',
    speaker: 'self',
    text: "Trois mille deux cent quarante-sept. Tu n'as pas trois mille euros. Tu n'as même pas trois cents euros. Le compteur des trente jours tourne déjà.",
    next: 'first_choice',
  },

  first_choice: {
    id: 'first_choice',
    act: ACTS.ACT_1,
    treeLabel: 'Que faire ?',
    type: 'choice',
    speaker: 'narrator',
    text: "Tu as quatre options. Comme toujours, l'inaction est la plus tentante.",
    choices: [
      { text: "Foncer à la CAF en personne", next: 'caf_arrival' },
      { text: "Composer le 3230 (numéro non surtaxé)", next: 'phone_hell' },
      { text: "Fouiller tes vieux papiers d'abord", next: 'search_archives' },
      { text: "Ranger la lettre dans le tiroir « plus tard »", next: 'denial_path' },
    ],
  },

  // ============ Branch: Denial (bad ending) ============

  denial_path: {
    id: 'denial_path',
    act: ACTS.ACT_1,
    treeLabel: 'Le tiroir',
    type: 'narrative',
    speaker: 'narrator',
    text: "Tu jettes la lettre dans le tiroir « plus tard ». Le tiroir est plein. Trois semaines passent. Le 31e jour, ta carte bancaire est avalée par le distributeur. Saisie sur compte. Tu es à -3 247 €. Tu n'as pas de plan B.",
    next: 'ending_denial',
  },

  ending_denial: {
    id: 'ending_denial',
    act: ACTS.ENDINGS,
    treeLabel: 'FIN · Déni',
    type: 'ending',
    ending: {
      id: 'denial',
      title: "Le Déni",
      type: 'bad',
      text: "Tu n'as rien fait. Le système t'a écrasé. Comme d'habitude. Score administratif : 0/10. Le facteur sonne à nouveau, dans deux semaines, avec un autre recommandé.",
    },
  },

  // ============ Branch: Search Archives ============

  search_archives: {
    id: 'search_archives',
    act: ACTS.ACT_2,
    treeLabel: 'Archives',
    type: 'narrative',
    speaker: 'narrator',
    text: "Tu vides la grande boîte « IMPORTANT » que tu n'as pas ouverte depuis 2017. Tu en sors : trois RIB périmés, une carte vitale enfantine, un avis d'imposition de 2019. Tu vérifies les chiffres. Ils ne collent pas. La CAF s'est trompée. Toi, tu en as la preuve.",
    next: 'archive_choice',
  },

  archive_choice: {
    id: 'archive_choice',
    act: ACTS.ACT_2,
    treeLabel: 'Atout en main',
    type: 'choice',
    speaker: 'self',
    text: "Tu tiens un atout. Comment l'utiliser ?",
    choices: [
      { text: "Photocopier en triple exemplaire", next: 'photocopie_grind' },
      { text: "Foncer à la CAF avec l'original", next: 'caf_arrival_with_proof' },
      { text: "Le scanner et l'envoyer par email", next: 'mail_proof' },
    ],
  },

  photocopie_grind: {
    id: 'photocopie_grind',
    act: ACTS.ACT_2,
    treeLabel: 'Photocopies',
    type: 'narrative',
    speaker: 'narrator',
    text: "Tu trouves un tabac qui fait photocopies. 0,30 € l'unité. Tu en fais cinq : une pour la CAF, une pour toi, une pour ton avocat imaginaire, une pour ta mère, une au cas où. Le buraliste te regarde bizarrement. Tu te sens prêt.",
    next: 'caf_arrival_with_proof',
  },

  mail_proof: {
    id: 'mail_proof',
    act: ACTS.ACT_2,
    treeLabel: 'Email CAF',
    type: 'narrative',
    speaker: 'narrator',
    text: "Tu envoies le scan via le formulaire de contact CAF. Trente secondes plus tard, un mail automatique te répond : « Votre demande sera traitée sous 6 à 8 semaines ». Tu n'as plus que 29 jours. Il faut y aller en personne.",
    next: 'caf_arrival_with_proof',
  },

  // ============ Branch: Phone Hell ============

  phone_hell: {
    id: 'phone_hell',
    act: ACTS.ACT_2,
    treeLabel: 'Le 3230',
    type: 'narrative',
    speaker: 'narrator',
    text: "Tu composes le 3230. Une voix robotique te propose un menu de douze options. Tu tapes 4. Puis 2. Puis 1. Puis 8. Tu retombes sur le menu principal. Tu recommences. Quarante-sept minutes plus tard : « ERREUR 500 — VEUILLEZ RAPPELER ULTÉRIEUREMENT ». *clic*",
    next: 'phone_choice',
  },

  phone_choice: {
    id: 'phone_choice',
    act: ACTS.ACT_2,
    treeLabel: 'Combiné moite',
    type: 'choice',
    speaker: 'self',
    text: "Le combiné est moite. Une heure de ta vie en moins.",
    choices: [
      { text: "Rappeler. Encore et encore.", next: 'fight_bug' },
      { text: "Aller à la CAF directement", next: 'caf_arrival' },
      { text: "Lancer le téléphone par la fenêtre", next: 'home_retry' },
    ],
  },

  fight_bug: {
    id: 'fight_bug',
    act: ACTS.ACT_2,
    treeLabel: '⚔ Bug',
    type: 'combat',
    speaker: 'bug_info',
    text: "Au quatorzième essai, la voix robotique craque. Le serveur de la CAF prend forme devant toi, en pixels grésillants. ERROR 500. Il ne te lâchera pas sans combat.",
    combat: {
      bossId: 'bug_info',
      onWin: 'bug_secret',
      onLose: 'phone_giveup',
    },
  },

  bug_secret: {
    id: 'bug_secret',
    act: ACTS.ACT_2,
    treeLabel: 'PDF caché',
    type: 'narrative',
    speaker: 'narrator',
    text: "Le serveur s'éteint dans un dernier sursaut électronique. Avant de mourir, il crache sur ton imprimante un PDF mal scanné : « ERREUR DE SAISIE — Agent #4471 — 14/03/2019 ». Tu tiens la preuve. Le trop-perçu, c'est leur faute.",
    next: 'caf_arrival_with_proof',
  },

  phone_giveup: {
    id: 'phone_giveup',
    act: ACTS.ACT_2,
    treeLabel: 'Abandon tél.',
    type: 'narrative',
    speaker: 'narrator',
    text: "Le serveur résiste. Tu raccroches, vaincu, l'oreille rouge. Une heure et demie de ta vie en moins, et toujours rien.",
    next: 'home_retry',
  },

  home_retry: {
    id: 'home_retry',
    act: ACTS.ACT_2,
    treeLabel: 'Retour maison',
    type: 'choice',
    speaker: 'narrator',
    text: "Tu es de retour à ton appartement. Le tiroir « plus tard » te fait de l'œil. Que fais-tu ?",
    choices: [
      { text: "Fouiller tes archives", next: 'search_archives' },
      { text: "Réessayer la CAF demain", next: 'caf_arrival' },
      { text: "Tout laisser tomber", next: 'denial_path' },
    ],
  },

  // ============ Branch: CAF visit (no proof) ============

  caf_arrival: {
    id: 'caf_arrival',
    act: ACTS.ACT_2,
    treeLabel: 'Arrivée CAF',
    type: 'narrative',
    speaker: 'narrator',
    text: "Tu arrives devant l'agence CAF. La queue commence sur le trottoir. Quarante-sept personnes devant toi. Tu prends ton ticket : N°212. Au-dessus du guichet, l'écran affiche 165. Tu t'assieds par terre.",
    next: 'caf_wait',
  },

  caf_wait: {
    id: 'caf_wait',
    act: ACTS.ACT_2,
    treeLabel: 'Trois heures',
    type: 'narrative',
    speaker: 'narrator',
    text: "Trois heures plus tard, ton tour arrive. Guichet 7. La Dame de l'Accueil te dévisage par-dessus ses lunettes en demi-lune. Elle a vu pire. Elle a vu mieux. Elle est lasse.",
    next: 'dame_choice',
  },

  dame_choice: {
    id: 'dame_choice',
    act: ACTS.ACT_2,
    treeLabel: 'La Dame',
    type: 'choice',
    speaker: 'dame_accueil',
    text: "« C'est pour quoi ? » *long soupir* « Vous avez un numéro de dossier ? »",
    choices: [
      { text: "Sortir un dossier proprement classé", next: 'dame_clean_papers' },
      { text: "Pleurer pour de vrai", next: 'dame_cry' },
      { text: "Exiger de voir un superviseur", next: 'fight_dame' },
    ],
  },

  dame_clean_papers: {
    id: 'dame_clean_papers',
    act: ACTS.ACT_2,
    treeLabel: 'Beau dossier',
    type: 'narrative',
    speaker: 'dame_accueil',
    text: "Elle hoche la tête, presque impressionnée par ton organisation. « Bon. Bureau 12B. Étage 3. Ascenseur en panne. Bon courage. » Elle tamponne ton ticket d'un geste sec.",
    next: 'inspector_intro',
  },

  dame_cry: {
    id: 'dame_cry',
    act: ACTS.ACT_2,
    treeLabel: 'Larmes',
    type: 'narrative',
    speaker: 'narrator',
    text: "Tes larmes font des taches sur le formulaire. La Dame de l'Accueil pince les lèvres. Quelque chose s'amollit dans son regard, juste une seconde. « Bureau 12B », murmure-t-elle, presque humaine. « Et arrêtez de chialer, c'est gênant pour les autres. »",
    next: 'inspector_intro',
  },

  fight_dame: {
    id: 'fight_dame',
    act: ACTS.ACT_2,
    treeLabel: '⚔ Dame',
    type: 'combat',
    speaker: 'dame_accueil',
    text: "Elle se lève lentement. Le panneau « FERMÉ — PAUSE » descend sur le guichet 7. Les autres usagers reculent d'un pas. C'est elle ou toi.",
    combat: {
      bossId: 'dame_accueil',
      onWin: 'dame_defeated',
      onLose: 'fled_home_dame',
    },
  },

  dame_defeated: {
    id: 'dame_defeated',
    act: ACTS.ACT_2,
    treeLabel: 'Dame K.O.',
    type: 'narrative',
    speaker: 'narrator',
    text: "La Dame de l'Accueil s'effondre sur sa chaise, vaincue par ta persévérance bureaucratique. Elle griffonne un mot tremblant : « Bureau 12B. Et qu'on ne le revoie plus jamais ici. »",
    next: 'inspector_intro',
  },

  fled_home_dame: {
    id: 'fled_home_dame',
    act: ACTS.ACT_2,
    treeLabel: 'Fuite Dame',
    type: 'narrative',
    speaker: 'narrator',
    text: "Vaincu par la Dame, tu rentres en métro avec dix-huit minutes de regret et un sandwich SNCF froid. Le compteur des trente jours continue de tourner.",
    next: 'home_retry',
  },

  // ============ Branch: CAF visit (with proof) ============

  caf_arrival_with_proof: {
    id: 'caf_arrival_with_proof',
    act: ACTS.ACT_3,
    treeLabel: 'CAF + preuve',
    type: 'narrative',
    speaker: 'narrator',
    text: "Cette fois, tu arrives à la CAF avec un dossier béton. Tu doubles la queue en agitant ton avis d'imposition comme un drapeau de paix. La Dame de l'Accueil te jette un regard, voit le tampon officiel, capitule sans soupirer. « Bureau 12B. »",
    next: 'inspector_intro_with_proof',
  },

  // ============ ACT 3 — Inspector ============

  inspector_intro: {
    id: 'inspector_intro',
    act: ACTS.ACT_3,
    treeLabel: 'Bureau 12B',
    type: 'narrative',
    speaker: 'inspecteur',
    text: "Tu pousses la porte du Bureau 12B après trois étages d'escalier. Un homme en costume gris te fixe par-dessus sa calculatrice. Il ne te dit pas bonjour. « Asseyez-vous. J'ai quelques... questions concernant vos revenus. »",
    next: 'inspector_choice',
  },

  inspector_choice: {
    id: 'inspector_choice',
    act: ACTS.ACT_3,
    treeLabel: "L'inspecteur",
    type: 'choice',
    speaker: 'inspecteur',
    text: "« D'après mon écran, vous nous devez 3 247 euros. Comment souhaitez-vous procéder ? »",
    choices: [
      { text: "Bluffer en menaçant d'un avocat", next: 'fight_inspector' },
      { text: "Coopérer et signer pour payer", next: 'ending_payed' },
      { text: "Te lever et partir en courant", next: 'fled_inspector' },
    ],
  },

  inspector_intro_with_proof: {
    id: 'inspector_intro_with_proof',
    act: ACTS.ACT_3,
    treeLabel: '12B + preuve',
    type: 'narrative',
    speaker: 'inspecteur',
    text: "Tu pousses la porte du Bureau 12B avec ta preuve serrée contre ta poitrine. L'inspecteur lève les yeux. Son sourire administratif se fissure légèrement quand il voit ton dossier. « Hm. Asseyez-vous. »",
    next: 'inspector_choice_with_proof',
  },

  inspector_choice_with_proof: {
    id: 'inspector_choice_with_proof',
    act: ACTS.ACT_3,
    treeLabel: 'Avis 2019',
    type: 'choice',
    speaker: 'self',
    text: "Tu poses ton avis d'imposition 2019 sur le bureau. L'inspecteur le lit. Lentement. Très lentement.",
    choices: [
      { text: "Attendre en silence, regard appuyé", next: 'inspector_capitulates' },
      { text: "Sourire poliment", next: 'inspector_capitulates' },
      { text: "Lui dire : « Votre erreur, pas la mienne. »", next: 'inspector_angry' },
    ],
  },

  inspector_capitulates: {
    id: 'inspector_capitulates',
    act: ACTS.ACT_3,
    treeLabel: 'Capitulation',
    type: 'narrative',
    speaker: 'inspecteur',
    text: "L'inspecteur retire ses lunettes. Les frotte. Les remet. « Ah. En effet. Il semble que... une erreur de saisie en mars 2019. Je vais classer votre dossier. Bonne journée. » Il tamponne. C'est fini.",
    next: 'ending_regularized',
  },

  inspector_angry: {
    id: 'inspector_angry',
    act: ACTS.ACT_3,
    treeLabel: 'Agacement',
    type: 'narrative',
    speaker: 'inspecteur',
    text: "Il pose lentement ton avis. Sourire glacial. « Je n'aime pas le ton. Erreur ou pas, l'argent a été versé sur votre compte. On va vérifier ÇA en détail. » Il décroche son téléphone et appelle la sécurité.",
    next: 'fight_inspector',
  },

  fight_inspector: {
    id: 'fight_inspector',
    act: ACTS.ACT_3,
    treeLabel: '⚔ Inspecteur',
    type: 'combat',
    speaker: 'inspecteur',
    text: "L'inspecteur ferme la porte du bureau à clé. Il sort sa calculatrice à 12 chiffres et son tampon « REFUSÉ ». C'est le moment de prouver ta valeur administrative.",
    combat: {
      bossId: 'inspecteur',
      onWin: 'inspector_defeated',
      onLose: 'huissier_call',
    },
  },

  inspector_defeated: {
    id: 'inspector_defeated',
    act: ACTS.ACT_3,
    treeLabel: 'Insp. K.O.',
    type: 'narrative',
    speaker: 'inspecteur',
    text: "L'inspecteur abandonne, exsangue. « Bon. D'accord. Je classe ça. Sortez. Je rentre chez moi. » Tu sors du Bureau 12B en silence, libre, victorieux. La porte claque derrière toi.",
    next: 'ending_regularized',
  },

  fled_inspector: {
    id: 'fled_inspector',
    act: ACTS.ACT_3,
    treeLabel: 'Fuite couloir',
    type: 'narrative',
    speaker: 'narrator',
    text: "Tu te lèves brusquement, attrape ta veste, et tu cours dans le couloir. L'escalier. La rue. Le métro. Tu rentres chez toi. Mais l'inspecteur a ton dossier, et ton adresse. Le compteur tourne désormais en accéléré.",
    next: 'huissier_call',
  },

  // ============ ACT 4 — Huissier ============

  huissier_call: {
    id: 'huissier_call',
    act: ACTS.ACT_4,
    treeLabel: "L'huissier",
    type: 'narrative',
    speaker: 'narrator',
    text: "Une semaine plus tard. Tu es chez toi, à manger des pâtes. La sonnette retentit. Sur le palier : un homme en costume noir avec un attaché-case. « Bonjour. Huissier de justice. J'ai un courrier recommandé pour vous. Veuillez signer. »",
    next: 'fight_courrier',
  },

  fight_courrier: {
    id: 'fight_courrier',
    act: ACTS.ACT_4,
    treeLabel: '⚔ Courrier CAF',
    type: 'combat',
    speaker: 'courrier_caf',
    text: "Le huissier ouvre son attaché-case. Une enveloppe géante en jaillit, sceau rouge sang, AR n°2C 047 892 3741 2. C'est le boss final, et il est venu chercher son dû. Plus de fuite possible.",
    combat: {
      bossId: 'courrier_caf',
      onWin: 'ending_outlaw',
      onLose: 'ending_ruined',
    },
  },

  // ============ ENDINGS ============

  ending_payed: {
    id: 'ending_payed',
    act: ACTS.ENDINGS,
    treeLabel: 'FIN · Payé',
    type: 'ending',
    ending: {
      id: 'payed',
      title: "L'Élève Modèle",
      type: 'neutral',
      text: "Tu signes le formulaire de prélèvement automatique. 24 mois. La CAF est satisfaite. Ta tirelire est vide. Tu manges des pâtes pendant deux ans, mais tu dors la conscience tranquille. Le système a gagné — avec ton consentement écrit, dûment tamponné.",
    },
  },

  ending_regularized: {
    id: 'ending_regularized',
    act: ACTS.ENDINGS,
    treeLabel: 'FIN · Régularisé',
    type: 'ending',
    ending: {
      id: 'regularized',
      title: "Régularisé",
      type: 'good',
      text: "Ton dossier est officiellement classé sans suite. La CAF reconnaît son erreur (sans le dire). Tu n'as rien payé. Tu rentres chez toi avec ton avis d'imposition 2019 plié dans la poche, comme un trophée froissé. Ce soir, tu commandes une pizza.",
    },
  },

  ending_outlaw: {
    id: 'ending_outlaw',
    act: ACTS.ENDINGS,
    treeLabel: 'FIN · Larzac',
    type: 'ending',
    ending: {
      id: 'outlaw',
      title: "Hors-la-Loi",
      type: 'rebel',
      text: "Tu as battu le huissier sur le pas de ta porte. Une semaine plus tard, tu vis dans une cabane dans le Larzac. Plus de compte bancaire, plus d'adresse fiscale, plus de CAF. Tu cultives des poireaux, tu lis Bakounine. C'est dur. Mais c'est libre.",
    },
  },

  ending_ruined: {
    id: 'ending_ruined',
    act: ACTS.ENDINGS,
    treeLabel: 'FIN · Saisie',
    type: 'ending',
    ending: {
      id: 'ruined',
      title: "Saisie Totale",
      type: 'bad',
      text: "Le huissier a tout pris. Mobilier, livret A, dignité, cafetière. Tu signes pour 36 mois de prélèvement. Tu n'es plus une personne, tu es un dossier qui s'autorégule. Ton numéro de sécu fait désormais partie d'un tableau Excel à la DGFiP, dans une cellule jaune avec un commentaire « PRIORITAIRE ».",
    },
  },
};

// Useful for the codex screen
export function getAllNodeIds() {
  return Object.keys(storyNodes);
}

export function getNodesByAct() {
  const grouped = {};
  for (const node of Object.values(storyNodes)) {
    const act = node.act || 'Sans acte';
    if (!grouped[act]) grouped[act] = [];
    grouped[act].push(node);
  }
  return grouped;
}

export function getEndings() {
  return Object.values(storyNodes).filter((n) => n.type === 'ending');
}
